import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#06080d] text-[#f0f2f7]" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b border-[#252a37] bg-[#10131c] sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#8a93a3] hover:text-[#f0f2f7]"
            data-testid="privacy-back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="wordmark text-lg">BTCCP</span>
          <span className="text-[#252a37]">—</span>
          <span className="text-sm text-[#8a93a3]">Privacy Policy</span>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-8 w-8 text-[#f5c518]" />
          <div>
            <h1 className="text-3xl font-bold text-[#f0f2f7]">Privacy Policy</h1>
            <p className="text-[#8a93a3] text-sm mt-1">Last updated: February 2026</p>
          </div>
        </div>

        <div className="space-y-8 text-[#8a93a3] leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">1. Introduction</h2>
            <p>
              BTCCP ("we", "our", "us") is a multi-organisation sports tournament management
              platform operated by Sandeep Maheshwari. This Privacy Policy explains how we
              collect, use, and protect information about users of the BTCCP mobile app and
              admin web panel.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">
              2. Information We Collect
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <span className="text-[#f0f2f7]">Phone number</span> — used for account
                identification and invite-based authentication.
              </li>
              <li>
                <span className="text-[#f0f2f7]">Display name</span> — optional, provided
                during account setup.
              </li>
              <li>
                <span className="text-[#f0f2f7]">Tournament data</span> — match scores, player
                registrations, team rosters entered by org admins and scorers.
              </li>
              <li>
                <span className="text-[#f0f2f7]">Usage logs</span> — timestamps of logins and
                scoring actions for audit purposes.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                To authenticate you and grant access to your organisation's data.
              </li>
              <li>
                To record and display live tournament scores and standings.
              </li>
              <li>
                To maintain audit logs for organisation administrators.
              </li>
              <li>
                We do not sell or share your data with third parties for marketing.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">4. Data Storage</h2>
            <p>
              All data is stored securely on servers within India. Passwords are hashed and
              never stored in plain text. Access tokens use industry-standard JWT with short
              expiry and refresh token rotation.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">5. Account Deletion</h2>
            <p>
              You may request deletion of your account and all associated personal data at any
              time. In the app, navigate to{' '}
              <span className="text-[#f0f2f7]">Profile → Account → Delete Account</span>. Data
              will be permanently removed within 30 days of the request.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">6. Your Rights</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your account and data.</li>
              <li>Object to or restrict certain processing.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">7. Contact</h2>
            <p>
              For any privacy-related queries or data deletion requests, contact:
              <br />
              <span className="text-[#f5c518] font-medium">Sandeep Maheshwari</span> — BTCCP /
              Circles
              <br />
              You may also use the in-app deletion flow described in Section 5.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#f0f2f7] mb-3">
              8. Changes to This Policy
            </h2>
            <p>
              We may update this policy periodically. Significant changes will be communicated
              via the app. Continued use of BTCCP after changes constitutes acceptance of the
              updated policy.
            </p>
          </section>
        </div>
      </main>

      <footer className="py-8 px-6 text-center text-xs text-[#8a93a3] border-t border-[#252a37] mt-8">
        © Sandeep Maheshwari — BTCCP
      </footer>
    </div>
  );
}
